<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Copy, ThumbsUp, ThumbsDown } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useDecodeMessage } from "@composables/decodeMessage";
  import { useToastsStore } from "@/stores/toastsStore";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
  import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";
  import { useQuoteSourceStore } from "@stores/survey/assistant/quoteSource";

  const props = defineProps({ index: { type: Number, required: true }, message: { type: Object, required: true } });

  const { decodeAiInsightsMessage } = useDecodeMessage();
  const toast = useToastsStore();

  const { isGeneratingMessage } = storeToRefs(useAiInsightConversationsStore());

  const aiInsightConversationMessagesStore = useAiInsightConversationMessagesStore();
  const { conversationMessages } = storeToRefs(aiInsightConversationMessagesStore);
  const { updateMessageFeedback, getMessageFeedback } = aiInsightConversationMessagesStore;

  const isLastMessage = (index) => index === conversationMessages.value.length - 1;

  const isTyping = computed(() => !props.message.answer && isLastMessage(props.index) && isGeneratingMessage.value);

  const isFetchingData = computed(() => props.message.action === "function_call");

  const lastMessageIsNotGenerating = (index) =>
    props.message.answer &&
    props.message.answer !== "err" &&
    (!isLastMessage(index) || (isLastMessage(index) && !isGeneratingMessage.value));

  const onCopy = (message) => {
    navigator.clipboard.writeText(decodeAiInsightsMessage(message.answer, "string"));
    toast.info("Copied to clipboard");
  };

  const { openQuoteSource } = useQuoteSourceStore();

  // Quotes are injected by decodeAiInsightsMessage inside v-html as a self-contained
  // .assistant-quote block. The whole block is clickable, resolved via delegation.
  const onAnswerClick = (event) => {
    const quote = event.target.closest(".assistant-quote");
    if (!quote) return;
    const messageId = quote.dataset.messageId ? Number(quote.dataset.messageId) : null;
    openQuoteSource(quote.dataset.respondentToken, messageId);
  };
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Data fetching / analysis in progress -->
    <div
      v-if="isFetchingData"
      class="inline-flex items-center gap-2 text-xs">
      <span class="relative flex h-2 w-2 shrink-0">
        <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span class="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <span class="animate-pulse font-medium text-primary">Fetching and analysing survey data</span>
    </div>

    <!-- Typing indicator (answer not started yet) -->
    <div
      v-else-if="isTyping"
      class="flex items-center gap-1 py-1"
      data-cy="assistant-typing">
      <span
        class="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60"
        style="animation-delay: 0ms" />
      <span
        class="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60"
        style="animation-delay: 150ms" />
      <span
        class="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60"
        style="animation-delay: 300ms" />
    </div>

    <!-- Error -->
    <div
      v-else-if="message.answer === 'err'"
      class="text-sm text-destructive">
      An error occurred. If this issue persists, please reach out to your main point of contact at Episto.
    </div>

    <!-- Answer (rendered markdown) -->
    <div
      v-else
      class="ai-insights-answer prose prose-sm max-w-none break-words text-sm leading-relaxed text-foreground"
      @click="onAnswerClick"
      v-html="decodeAiInsightsMessage(message.answer, 'html')"></div>

    <!-- Feedback actions -->
    <div
      v-if="lastMessageIsNotGenerating(index)"
      class="flex items-center gap-1">
      <Tooltip content="Copy">
        <Button
          variant="ghost"
          size="icon-sm"
          @click="onCopy(message)">
          <Copy class="h-4 w-4" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="getMessageFeedback(index) !== 'bad'"
        content="Good response">
        <Button
          variant="ghost"
          size="icon-sm"
          @click="updateMessageFeedback(index, 'good')">
          <ThumbsUp
            class="h-4 w-4"
            :class="{ 'fill-current text-primary': getMessageFeedback(index) === 'good' }" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="getMessageFeedback(index) !== 'good'"
        content="Bad response">
        <Button
          variant="ghost"
          size="icon-sm"
          @click="updateMessageFeedback(index, 'bad')">
          <ThumbsDown
            class="h-4 w-4"
            :class="{ 'fill-current text-primary': getMessageFeedback(index) === 'bad' }" />
        </Button>
      </Tooltip>
    </div>
  </div>
</template>

<style scoped>
  /* Inline hints injected by decodeAiInsightsMessage (e.g. "(B5)", "Respondent #AB12")
     stay visually secondary now that the legacy SCSS is gone. */
  .ai-insights-answer :deep(.assistant-message__question-item__text--hint) {
    opacity: 0.6;
    font-size: 0.92em;
  }

  /* Full-width, fully-clickable quote block: italic text on the left, respondent badge right.
     Colors reference the project's oklch design tokens directly (var(--x), not hsl()). */
  .ai-insights-answer :deep(.assistant-quote) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.5rem 0;
    padding: 0.625rem 0.875rem;
    border-left: 3px solid var(--border);
    border-radius: 0 0.5rem 0.5rem 0;
    background: color-mix(in oklch, var(--muted), var(--background) 55%);
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  .ai-insights-answer :deep(.assistant-quote:hover) {
    border-left-color: color-mix(in oklch, var(--foreground), var(--background) 60%);
    background: color-mix(in oklch, var(--muted), var(--background) 30%);
  }

  .ai-insights-answer :deep(.assistant-quote__text) {
    font-style: italic;
    color: var(--foreground);
  }

  /* Respondent id badge on the right of the quote block. */
  .ai-insights-answer :deep(.assistant-quote__badge) {
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    border-radius: 0.375rem;
    background: var(--background);
    border: 1px solid var(--border);
    font-size: 0.78em;
    font-weight: 500;
    color: var(--muted-foreground);
  }

  /* The model emits quotes as bullet list items — drop the bullet/indent so the
     quote blocks read as standalone cards. The prose-sm list indent is cancelled per item
     so plain bullets sharing the list keep theirs. */
  .ai-insights-answer :deep(li:has(> .assistant-quote, > p > .assistant-quote)) {
    list-style: none;
    margin-inline-start: -1.5714286em;
  }

  .ai-insights-answer :deep(ul:has(> li > .assistant-quote, > li > p > .assistant-quote)) {
    margin: 0;
  }
</style>
